import { UserPlus } from 'lucide-react';
import { Alert, AlertDescription } from '@clay-run/glaze/alert';
import { Button } from '@clay-run/glaze/button';
import { Text } from '@clay-run/glaze/text';
import type { ContactRow } from '@shared/flow/contact-view';
import { FlowTable } from '../FlowTable';
import { BulkEnrollPopover } from './BulkEnrollPopover';
import { SignalScopePrompt } from './SignalScopePrompt';
import { useContactTableModel, type ContactFlowTableProps } from './useContactTableModel';

/**
 * The bound contact mount: seeded presets + contact column config + the
 * /api/flow/contacts fetch, composed onto the generic FlowTable. Consumers
 * stay thin — Command Center's Contact-first view and the main-Terra account
 * Contact tab both just render <ContactFlowTable [accountId]/>.
 *
 * A thin renderer (plan 2026-09-27-007 U2b, D1): everything between
 * `useContactMount` and the JSX — mount facts, the view machine, `?contact=`,
 * signal narrowing, pills, selection, jump-to-row, the FlowTable config —
 * lives in `useContactTableModel` so a V2 mount can call the same hook
 * without forking it. This file keeps the remount key and today's JSX.
 */

export type { ContactFlowTableProps };

/**
 * U2 / R1(b) (2026-08-27) — the account identity is a REMOUNT boundary.
 *
 * `useViewStateMachine`'s first-resolution guard (`resolvedRef`) is
 * lifetime-scoped on purpose, so `skipWorkingRestore` only ever fires once
 * per mount and `disableFilterCarry` only fires through `openView`. Neither
 * can react to `accountId` changing underneath a LIVE table — and the
 * main-Terra Contacts tab does exactly that: V4AccountsRoute's
 * `deriveTabContent` keeps `content.kind === 'contact'` and swaps only
 * `accountId`, so React reconciles `<ContactTab/>` by position and account
 * A's dirty working state (`machine.working`) rides straight into account B's
 * query. The account drill-down avoids it only by accident (its `isLoading`
 * branch unmounts the subtree between accounts).
 *
 * Keying here rather than at either call site makes "an account-scoped table
 * starts from its view's own clean definition" a property of the component
 * itself, so a future in-place mount can't silently reintroduce the carry.
 * The global mount's key is a constant (`accountId` is always undefined), so
 * the Command Center table never remounts and its U9 restore /
 * 2026-08-12 carry-forward are untouched.
 *
 * The PLAYS SURFACE is deliberately NOT part of this key (plan
 * 2026-09-10-001 D8): the All People mount's filters are ordinary edits on
 * a live table, not a new identity. (Plan 2026-09-24-005 U15 removed the
 * imperative play-scope request; selecting a play now swaps this mount out
 * for the "People in <Play>" sections instead.)
 */
//
// A play-section mount's selected play AND section (`playScope`, plan
// 2026-09-25-002 U6; first `notInPlay.playId`, plan 2026-09-24-005 U16) ARE
// part of the key: unlike a scope chip, a play switch there changes the
// table's universe, so it remounts (fresh search, selection and
// first-resolution guard) and refetches for the new play.
export function ContactFlowTable(props: ContactFlowTableProps) {
  return <ContactFlowTableForAccount key={`${props.accountId ?? ''}|${props.playScope?.playId ?? ''}|${props.playScope?.section ?? ''}`} {...props} />;
}

function ContactFlowTableForAccount(props: ContactFlowTableProps) {
  const { fillHeight } = props;
  const { tableProps, bulk, banners } = useContactTableModel(props);

  // A13 (QA round 2) — Bulk actions itself moved ONTO FlowTable's toolbar row
  // (`toolbarTrailing`, right of the "N loaded" count). What's left in the
  // strip above the table is the selection bookkeeping that has nowhere else
  // to go: "Add k/n to queue", Clear, and Select-all-matching. All three are
  // already conditional, so with nothing selected and no next page the strip
  // rendered as an EMPTY 12px-tall flex row plus the wrapper's `gap-2` —
  // dead vertical space above every People table. Gate the whole row on
  // "does it have anything to say", rather than leaving an empty div.
  const showBulkStrip = bulk.selectedIds.size > 0 || bulk.hasNextPage;

  // Hoisted out of the JSX so the SAME element goes to `toolbarTrailing`
  // (and so the popover is constructed exactly once, not once per branch).
  // Also hosts the Custom Invite stopgap toolbar button (plan
  // 2026-09-23-custom-invite-stopgap-plan.md, U6, D3), Plays surface only.
  const bulkActions = (
    <div className="flex items-center gap-2">
      {/* Custom Invite stopgap (plan 2026-09-23, D3): both Plays-tab surfaces —
          same `isPlaySurface(mount)` gate as `useSignalSubtypeOptions` (both
          in useContactTableModel, via `bulk.showCustomInvite`).
          Opens the dock on the Custom Invite tab; the panel owns the rest. */}
      {bulk.showCustomInvite && (
        <Button
          variant="secondary"
          size="sm"
          onClick={bulk.openCustomInvite}
          title="Invite someone by email who has no Salesforce contact yet"
        >
          <span className="inline-flex items-center gap-1.5">
            <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
            Custom Invite
          </span>
        </Button>
      )}
      <BulkEnrollPopover
        selectedIds={bulk.orderedSelectedIds}
        queuedContactIds={bulk.queuedContactIds}
        emailByContactId={bulk.emailByContactId}
        // U6 scope 2 (Decision 15) — see `bulk.onCommitted` in useContactTableModel.
        onCommitted={bulk.onCommitted}
      />
    </div>
  );

  return (
    // The wrapper preserves FlowTable's `fillHeight` contract (its own doc
    // comment: "every ancestor between the scrolling pane and this
    // component must be flex flex-col min-h-0") — the bulk selection strip
    // sits above FlowTable as a fixed-height sibling inside the same flex
    // column, and is absent entirely when it has nothing to show (A13).
    <div className={`flex flex-col min-h-0 gap-2 ${fillHeight ? 'flex-1' : ''}`}>
      {showBulkStrip && (
      <div className="flex items-center gap-3">
        {bulk.selectedIds.size > 0 && (
          <>
            {/* D5 — always shows "Add {addable}/{n} to queue" (even at
                k === 0, e.g. "Add 15/15 to queue"), with a tertiary
                "{k} already queued" line beneath ONLY when k > 0. Lives here
                in the toolbar strip, not on the `BulkEnrollPopover` trigger
                itself (that button's own "Bulk actions (n)" label is
                unchanged — see BulkEnrollPopover.tsx). */}
            <div className="flex flex-col leading-tight">
              <Text size="xs" variant="secondary">{`Add ${bulk.addableCount}/${bulk.selectedIds.size} to queue`}</Text>
              {bulk.selectedAlreadyQueued > 0 && (
                <Text size="xs" variant="tertiary">{`${bulk.selectedAlreadyQueued} already queued`}</Text>
              )}
            </div>
            <button
              type="button"
              onClick={bulk.clear}
              className="text-xs text-content-action hover:underline"
            >
              Clear
            </button>
          </>
        )}
        {bulk.hasNextPage && (
          <Button variant="ghost" size="xs" loading={bulk.selectingAll} disabled={bulk.selectingAll} onClick={() => void bulk.selectAllMatching()}>
            {bulk.selectingAll ? 'Loading all contacts…' : 'Select all matching'}
          </Button>
        )}
      </div>
      )}
      {/* U17 (Item C, D9) — soft, non-dismissable warning; `effectiveFilters`
          is already `[]` while bypassed, so a bypassed deep link never warns. */}
      {banners.signalGroupCount > 1 && (
        <div data-testid="signal-split-groups-warning">
          <Alert status="warning">
            <AlertDescription>These Signal filters are in different groups, so they may match different signals.</AlertDescription>
          </Alert>
        </div>
      )}
      {banners.pendingSignalIntent && (
        <SignalScopePrompt
          kind={banners.pendingSignalIntent.kind}
          options={banners.signalSubtypeOptions}
          blockedReason={banners.scopeBlockedReason ?? undefined}
          onApply={banners.applySignalScope}
          onCancel={banners.dismissSignalIntent}
        />
      )}
      <FlowTable<ContactRow>
        {...tableProps}
        // A13 — right of the "N loaded" count, on the search row.
        toolbarTrailing={bulkActions}
      />
    </div>
  );
}
